"use client";

import { NavigationItem } from "@/helpers/enums";
import { fadeInAnimation } from "@/helpers/fadeInAnimation";
import {
  localizedPath,
  localeFromParams,
  stripLocaleFromPathname,
} from "@/lib/i18n/navigation";
import { expertiseSlugs, slRoutes } from "@/lib/slRoutes";
import { teamMembers } from "@/lib/teamData";
import { Box, ContainerProps, useMediaQuery, useTheme } from "@mui/material";
import { m, useInView } from "motion/react";
import { useParams, usePathname, useRouter } from "next/navigation";
import { RefObject, useCallback, useState } from "react";
import { SLNavigationFlyoutMenus } from "./SLNavigationFlyoutMenus";
import { SLNavigationLogo } from "./SLNavigationLogo";
import { SLNavigationMainItems } from "./SLNavigationMainItems";
import { SLNavigationMobileDrawer } from "./SLNavigationMobileDrawer";

export function SLNavigation({
  contentContainerMaxWidth,
  headerContainerRef,
  expertiseRef,
  aboutRef,
  contactRef,
  disableEntryAnimation = false,
}: {
  contentContainerMaxWidth: ContainerProps["maxWidth"];
  headerContainerRef?: RefObject<HTMLDivElement | null>;
  expertiseRef?: RefObject<HTMLDivElement | null>;
  aboutRef?: RefObject<HTMLDivElement | null>;
  contactRef: RefObject<HTMLDivElement | null>;
  disableEntryAnimation?: boolean;
}) {
  const isAnimationActive = false;
  const isInView = useInView(headerContainerRef || { current: null });
  const expertiseInView = useInView(expertiseRef || { current: null });
  const aboutInView = useInView(aboutRef || { current: null });
  const contactInView = useInView(contactRef);

  const [burgerOpen, setBurgerOpen] = useState(false);
  const handleBurgerClose = useCallback(() => setBurgerOpen(false), []);

  const router = useRouter();
  const params = useParams();
  const theme = useTheme();
  const isDesktopNav = useMediaQuery(theme.breakpoints.up("md"));
  const pathname = usePathname();
  const locale = localeFromParams(params.locale);
  const pathSansLocale = stripLocaleFromPathname(pathname);

  const handleClickScroll = useCallback(
    (id: string) => {
      const element = document.getElementById(id);
      if (!element) {
        router.push(`${localizedPath("/", locale)}#${id}`);
      }
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
      handleBurgerClose();
    },
    [router, handleBurgerClose, locale],
  );

  const areNavigationItemsInView = {
    [NavigationItem.Expertise]: expertiseInView,
    [NavigationItem.About]: aboutInView,
    [NavigationItem.Blog]: undefined,
    [NavigationItem.Contact]: contactInView,
  };

  const navItemSlugMapping = {
    [NavigationItem.Expertise]: Object.values(expertiseSlugs),
    [NavigationItem.About]: Object.values(teamMembers).map(
      (member) => member.slug,
    ),
    [NavigationItem.Blog]: undefined,
    [NavigationItem.Contact]: undefined,
  };

  const isPageInView = (id: string): boolean => {
    return Boolean(
      navItemSlugMapping[id as NavigationItem]?.some(
        (slug) =>
          pathSansLocale.startsWith(slRoutes.expertiseField(slug)) ||
          pathSansLocale.startsWith(slRoutes.teamMember(slug)),
      ),
    );
  };

  const isCurrentPage = (item: {
    id: string | null;
    name: string;
  }): boolean => {
    if (!item.id) return pathSansLocale.startsWith("/blog");
    return Boolean(
      isPageInView(item.id) ||
        areNavigationItemsInView[item.id as NavigationItem],
    );
  };

  const navFontSize = "1.4rem";

  const [openTerminBuchen, setOpenTerminBuchen] = useState(false);

  const [anchorElExpertise, setAnchorElExpertise] =
    useState<null | HTMLElement>(null);
  const handleCloseExpertiseMenu = () => setAnchorElExpertise(null);
  const handleClickExpertiseMenu = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    setAnchorElExpertise(event.currentTarget);
  };

  const [anchorElAboutUs, setAnchorElAboutUs] = useState<null | HTMLElement>(
    null,
  );
  const handleCloseAboutUsMenu = () => setAnchorElAboutUs(null);
  const handleClickAboutUsMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorElAboutUs(event.currentTarget);
  };

  const handleClickOnNavigationItemWithSectionOnHomePage = (
    event: React.MouseEvent<HTMLDivElement>,
    id: string,
  ) => {
    if (id === NavigationItem.Expertise) {
      handleClickExpertiseMenu(event);
    }

    if (id === NavigationItem.About) {
      handleClickAboutUsMenu(event);
    }

    if (id === NavigationItem.Contact) {
      handleClickScroll(id);
    }
  };

  const mainItemsProps = {
    pathname: pathSansLocale,
    navFontSize,
    isCurrentPage,
    isPageInView,
    areNavigationItemsInView,
    onNavItemClick: handleClickOnNavigationItemWithSectionOnHomePage,
    languageVariant: "icon" as const,
  };

  return (
    <Box
      component={m.nav}
      role="navigation"
      aria-label="Main navigation"
      {...(disableEntryAnimation ? {} : fadeInAnimation)}
      {...(disableEntryAnimation ? {} : { layout: true })}
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        width: isAnimationActive ? "98%" : "100%",
        position: "sticky",
        top: isAnimationActive ? 16 : 0,
        zIndex: 2,
        background: theme.palette.background.default,
        borderRadius: isAnimationActive ? `${theme.shape.borderRadius}px` : 0,
        maxWidth:
          !isInView && isAnimationActive
            ? theme.breakpoints.values[
                contentContainerMaxWidth as keyof typeof theme.breakpoints.values
              ]
            : "100%",
        justifySelf: "center",
        alignItems: "center",
        maxHeight: isAnimationActive ? "100px" : "116px",
      }}
      sx={{
        boxShadow: !isInView && isAnimationActive ? 1 : 0,
        overflow: { xs: "hidden", md: "visible" },
        position: "relative",
        pt: isAnimationActive ? 0 : "16px",
        px: "24px",
        py: 4,
      }}
    >
      <SLNavigationLogo />

      {isDesktopNav ? (
        <Box
          sx={{
            display: "contents",
          }}
        >
          <SLNavigationMainItems {...mainItemsProps} isMobile={false} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            width: "fit-content",
            height: "100%",
            alignSelf: "center",
          }}
        >
          <SLNavigationMobileDrawer
            burgerOpen={burgerOpen}
            onBurgerOpen={() => setBurgerOpen(true)}
            onBurgerClose={handleBurgerClose}
            openTerminBuchen={openTerminBuchen}
            onOpenTerminBuchen={() => setOpenTerminBuchen(true)}
            onCloseTerminBuchen={() => setOpenTerminBuchen(false)}
            navItems={
              <SLNavigationMainItems {...mainItemsProps} isMobile={true} />
            }
          />
        </Box>
      )}

      <SLNavigationFlyoutMenus
        pathname={pathSansLocale}
        locale={locale}
        anchorElExpertise={anchorElExpertise}
        onCloseExpertiseMenu={handleCloseExpertiseMenu}
        anchorElAboutUs={anchorElAboutUs}
        onCloseAboutUsMenu={handleCloseAboutUsMenu}
      />
    </Box>
  );
}
